import { createUseStyles } from 'react-jss';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  Session,
  StudyType,
  useIncentiveText,
  useIsParticipant,
} from '@app/api';
import {
  DurationChip,
  LabeledIconChip,
  LabeledSection,
  PublicBackgroundBlock,
  SessionFormatChip,
  useSesionMinAndMaxDatesString,
} from '@app/core';
import { HowItWorksLine } from '../HowItWorksLine';
import { TechnicalRequirements } from '../../../ParticipationSummary/components/TechnicalRequirements';
import { NotForYouBlock } from '../NotForYouBlock';
import { ReferralBlock } from '../ReferralBlock';
import { ParticipateButton } from '../ParticipateButton';
import { IncentiveBlock } from '../IncentiveBlock';

const useStyles = createUseStyles((theme: Theme) => ({
  studyInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 4,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 5,
    position: 'relative',
    zIndex: 1,

    width: '100%',
    maxWidth: theme.maxContentWidth.screen,

    margin: 'auto',
    [theme.media.mobile]: {
      boxShadow: 'none',
      maxWidth: '100%',
      gap: theme.marginBase * 7,
      padding: [[theme.marginBase * 3, theme.marginBase * 2]],
    },
  },
  content: {
    ...theme.fonts.caption,
    color: theme.colors.inkPrimary,
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    whiteSpace: 'pre-wrap',
  },
  descriptionContainer: {
    backgroundColor: theme.colors.grayBackground,
  },
  blueIcons: {
    color: theme.colors.blueAccessible,
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.marginBase * 3,
  },
  title: {
    ...theme.fonts.h3,
  },
  mobileWrap: {
    [theme.media.mobile]: {
      width: '100%',
    },
  },
  date: {
    ...theme.fonts.caption,
  },
}));

interface Props {
  session: Session;
  sessionLinkId?: string;
  participationId?: string | undefined;
  onClick: () => any;
  buttonIsLoading?: boolean;
  buttonIsDisabled?: boolean;
  participantId?: string;
}

export const StudyDescriptionOriginal = ({
  sessionLinkId,
  session,
  participationId,
  onClick,
  buttonIsLoading,
  buttonIsDisabled,
  participantId,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const incentiveText = useIncentiveText(sessionLinkId, session);
  const isParticipant = useIsParticipant();
  const studyDates = useSesionMinAndMaxDatesString(session, 'DD/MM/YY');

  const isModeratedStudy = session.studyType === StudyType.MODERATED;
  const isUnmoderatedStudy = session.studyType !== StudyType.MODERATED;

  return (
    <div className={classes.container}>
      {session.showHowItWorks && (
        <HowItWorksLine
          noIncentive={!incentiveText}
          isUnmoderated={isUnmoderatedStudy}
        />
      )}
      <PublicBackgroundBlock>
        <h1 className={classes.title}>{session.title}</h1>
        <div className={classes.infoRow}>
          <LabeledSection
            label={t('application.info.duration')}
            className={classes.mobileWrap}
          >
            <DurationChip session={session} color="blue" />
          </LabeledSection>
          {isUnmoderatedStudy ? (
            <LabeledIconChip
              label={t('application.info.studyType')}
              chipIcon="ri-list-check-2"
              chipLabel={t(
                `application.info.studyTypeList.${session.studyType}`,
              )}
              iconClassName={classes.blueIcons}
              className={classes.mobileWrap}
            />
          ) : session.format ? (
            <LabeledSection
              label={t('application.info.formats_one')}
              className={classes.mobileWrap}
            >
              <SessionFormatChip session={session} color="blue" />
            </LabeledSection>
          ) : null}
          {isModeratedStudy && (
            <LabeledSection label={t('application.info.dates')}>
              <p className={classes.date}>{studyDates}</p>
            </LabeledSection>
          )}
        </div>
        <LabeledSection label={t('application.info.description')}>
          <div
            className={classnames(
              classes.content,
              classes.descriptionContainer,
            )}
          >
            {session.description}
          </div>
        </LabeledSection>

        <IncentiveBlock session={session} sessionLinkId={sessionLinkId} />
        <TechnicalRequirements session={session} dynamicDevices />
        <ParticipateButton
          participationId={participationId}
          onClick={onClick}
          buttonIsLoading={buttonIsLoading}
          buttonIsDisabled={buttonIsDisabled}
        />
      </PublicBackgroundBlock>
      {!isParticipant && <NotForYouBlock />}
      <ReferralBlock session={session} participantId={participantId} />
    </div>
  );
};
